// getUserMedia only exists on secure origins — on an http:// host
// navigator.mediaDevices is undefined, which surfaces here as 'unsupported'
// and the caller has to fall back to the file picker.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useRef, useState } from 'react';
var JPEG_QUALITY = 0.8;
// Photos are stored as database blobs; 1080p keeps them a few hundred KB.
var MAX_CAPTURE_DIMENSION = 1920;
export var useCameraCapture = function (active) {
    var videoRef = useRef(null);
    var canvasRef = useRef(null);
    var streamRef = useRef(null);
    var _a = useState([]), cameras = _a[0], setCameras = _a[1];
    var _b = useState(''), deviceId = _b[0], setDeviceId = _b[1];
    var _c = useState(false), isReady = _c[0], setIsReady = _c[1];
    var _d = useState(false), isStarting = _d[0], setIsStarting = _d[1];
    var _e = useState(''), errorCode = _e[0], setErrorCode = _e[1];
    var stopCamera = useCallback(function () {
        var _a;
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach(function (track) { return track.stop(); });
        streamRef.current = null;
        setIsReady(false);
    }, []);
    var startCamera = useCallback(function (selected) { return __awaiter(void 0, void 0, void 0, function () {
        var stream, devices, videoDevices, reason_1, denied;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    if (!((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia)) {
                        setErrorCode('unsupported');
                        setIsStarting(false);
                        return [2 /*return*/];
                    }
                    setIsStarting(true);
                    setIsReady(false);
                    setErrorCode('');
                    stopCamera();
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 6, 7, 8]);
                    return [4 /*yield*/, navigator.mediaDevices.getUserMedia({
                            audio: false,
                            video: selected
                                ? { deviceId: { exact: selected }, width: { ideal: 1920 }, height: { ideal: 1080 } }
                                : {
                                    facingMode: { ideal: 'environment' },
                                    width: { ideal: 1920 },
                                    height: { ideal: 1080 }
                                }
                        })];
                case 2:
                    stream = _d.sent();
                    streamRef.current = stream;
                    if (!videoRef.current) return [3 /*break*/, 4];
                    videoRef.current.srcObject = stream;
                    return [4 /*yield*/, videoRef.current.play()];
                case 3:
                    _d.sent();
                    _d.label = 4;
                case 4: return [4 /*yield*/, navigator.mediaDevices.enumerateDevices()];
                case 5:
                    devices = _d.sent();
                    videoDevices = devices.filter(function (device) { return device.kind === 'videoinput'; });
                    setCameras(videoDevices.map(function (device, index) { return ({
                        deviceId: device.deviceId,
                        label: device.label || "Camera " + (index + 1)
                    }); }));
                    setDeviceId(((_b = stream.getVideoTracks()[0]) === null || _b === void 0 ? void 0 : _b.getSettings().deviceId) || ((_c = videoDevices[0]) === null || _c === void 0 ? void 0 : _c.deviceId) || '');
                    setIsReady(true);
                    return [3 /*break*/, 8];
                case 6:
                    reason_1 = _d.sent();
                    denied = reason_1 instanceof DOMException &&
                        (reason_1.name === 'NotAllowedError' || reason_1.name === 'SecurityError');
                    setErrorCode(denied ? 'denied' : 'failed');
                    return [3 /*break*/, 8];
                case 7:
                    setIsStarting(false);
                    return [7 /*endfinally*/];
                case 8: return [2 /*return*/];
            }
        });
    }); }, [stopCamera]);
    useEffect(function () {
        if (!active) {
            stopCamera();
            return undefined;
        }
        void startCamera();
        return stopCamera;
    }, [active, startCamera, stopCamera]);
    var capture = useCallback(function () { return __awaiter(void 0, void 0, Promise, function () {
        var video, canvas, scale, context;
        return __generator(this, function (_a) {
            video = videoRef.current;
            canvas = canvasRef.current;
            if (!video || !canvas || !isReady)
                return [2 /*return*/, null
                    // The ideal constraint is only a request, so enforce the cap here too.
                ];
            scale = Math.min(1, MAX_CAPTURE_DIMENSION / Math.max(video.videoWidth, video.videoHeight));
            canvas.width = Math.round(video.videoWidth * scale);
            canvas.height = Math.round(video.videoHeight * scale);
            context = canvas.getContext('2d');
            if (!context)
                return [2 /*return*/, null];
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            return [2 /*return*/, new Promise(function (resolve) {
                    canvas.toBlob(function (blob) { return resolve(blob); }, 'image/jpeg', JPEG_QUALITY);
                })];
        });
    }); }, [isReady]);
    return {
        videoRef: videoRef,
        canvasRef: canvasRef,
        cameras: cameras,
        deviceId: deviceId,
        isReady: isReady,
        isStarting: isStarting,
        errorCode: errorCode,
        startCamera: startCamera,
        stopCamera: stopCamera,
        capture: capture
    };
};
export default useCameraCapture;
